import { useCallback, useRef, useState } from 'react'
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react'

export default function ResizableImageComponent({ node, updateAttributes, selected }: NodeViewProps) {
  const { src, alt, title, width, height } = node.attrs
  const imgRef = useRef<HTMLImageElement>(null)
  const [isResizing, setIsResizing] = useState(false)

const startResize = useCallback(
  (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const img = imgRef.current
    if (!img) return

    const startX = e.clientX
    const startY = e.clientY
    const startWidth = img.getBoundingClientRect().width
    const aspectRatio = img.naturalHeight / img.naturalWidth || 1

    setIsResizing(true)

    const computeNewWidth = (clientX: number, clientY: number) => {
      const dx = clientX - startX
      const dy = clientY - startY
      // 가로 이동량(dx)과, 세로 이동량을 종횡비로 환산한 값(dy / aspectRatio) 중
      // 절댓값이 더 큰 쪽을 채택 → 가로/세로/대각선 어느 방향으로 끌어도 자연스럽게 반응
      const deltaFromX = dx
      const deltaFromY = dy / aspectRatio
      const delta = Math.abs(deltaFromX) > Math.abs(deltaFromY) ? deltaFromX : deltaFromY
      return Math.max(50, Math.round(startWidth + delta))
    }

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = computeNewWidth(moveEvent.clientX, moveEvent.clientY)
      if (imgRef.current) {
        imgRef.current.style.width = `${newWidth}px`
      }
    }

    const onMouseUp = (upEvent: MouseEvent) => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      setIsResizing(false)

      const newWidth = computeNewWidth(upEvent.clientX, upEvent.clientY)
      const newHeight = Math.round(newWidth * aspectRatio)

      updateAttributes({ width: newWidth, height: newHeight })
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  },
  [updateAttributes]
)

  return (
    <NodeViewWrapper
      as="span"
      className={`resizable-image-wrapper${selected ? ' is-selected' : ''}`}
      style={{ display: 'inline-block', position: 'relative', lineHeight: 0 }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        title={title}
        style={{
          width: width ? `${width}px` : 'auto',
          height: height ? `${height}px` : 'auto',
          maxWidth: '100%',
          display: 'block',
        }}
        draggable={false}
      />
      {selected && (
        <span
          className={`resizable-image-handle${isResizing ? ' is-active' : ''}`}
          onMouseDown={startResize}
        />
      )}
    </NodeViewWrapper>
  )
}